import {
  Button,
  Checkbox,
  Empty,
  Form,
  Input,
  Layout,
  Menu as AntMenu,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  ApartmentOutlined,
  AppstoreAddOutlined,
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  BgColorsOutlined,
  DeleteOutlined,
  EditOutlined,
  LogoutOutlined,
  MailOutlined,
  MenuOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createUser,
  createRole,
  deleteRole,
  deleteUser,
  getRoleMenuPermissions,
  listUserTypes,
  listMenus,
  listMenuPermissions,
  listUsers,
  getUsersFromRole,
  saveRoleMenuPermissions,
  updateUser,
  updateRole,
} from '../../services/adminCoreApi';
import {
  getAvailableCollections,
  getFieldConfigSeedStatus,
  seedFieldConfig,
} from '../../services/adminApi';
import { getStoredUser, logout } from '../../services/authApi';
import { isPermissionApplicable } from '../../utils/permissionApplicability';
import AppCard from '../../components/cards/AppCard';
import ParamListView from '../../components/ParamListView';
import ModuleFieldConfigEditor from '../Modules/Admin/components/ModuleFieldConfigEditor';
import RoleFormPermissionsEditor from '../Modules/Admin/components/RoleFormPermissionsEditor';
import RoleActionsEditor from '../Modules/Admin/components/RoleActionsEditor';
import FormGroupsSection from '../Modules/Admin/FormGroupsSection';
import DetailGroupsSection from '../Modules/Admin/DetailGroupsSection';
import ActionsSection from '../Modules/Admin/ActionsSection';
import TemplatesSection from '../Modules/Admin/TemplatesSection';
import AiServiceSettingsSection from '../Modules/Admin/AiServiceSettingsSection';
import IntegrationSettingsSection from '../Modules/Admin/IntegrationSettingsSection';
import AutomationsSection from '../Modules/Admin/AutomationsSection';
import WorkflowsSection from '../Modules/Admin/WorkflowsSection';
import MastersSection from '../Modules/Admin/MastersSection';
import MenusSection from './MenusSection';
import './AdminDashboard.css';

const { Content, Header, Sider } = Layout;
const { Text, Title } = Typography;

function normalizeRoleName(value) {
  return String(value ?? '').trim().replace(/[\s_-]+/g, ' ').toUpperCase();
}

function moduleTitle(value) {
  return String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function normalizeSeedKey(value) {
  return String(value || '').trim().toLowerCase();
}

function seedOptionValue(item) {
  return item?.key || item?.collectionName || item?.value || '';
}

function buildSeedOptions(statusModules = [], collections = []) {
  const options = [];
  const seenValues = new Set();
  const seededKeys = new Set();
  const moduleCollections = new Set();

  statusModules.forEach((module) => {
    const value = seedOptionValue(module);
    if (!value) return;

    const key = normalizeSeedKey(module.key || value);
    const collectionKey = normalizeSeedKey(module.collectionName);
    if (module.seeded) {
      seededKeys.add(key);
      if (collectionKey) seededKeys.add(collectionKey);
    }
    if (collectionKey) moduleCollections.add(collectionKey);

    seenValues.add(value);
    options.push({
      label: module.label || moduleTitle(value),
      value,
      disabled: Boolean(module.seeded),
    });
  });

  collections.forEach((collection) => {
    const value = String(collection || '').trim();
    if (!value || seenValues.has(value)) return;

    const collectionKey = normalizeSeedKey(value);
    if (moduleCollections.has(collectionKey) || seededKeys.has(collectionKey)) return;

    seenValues.add(value);
    options.push({
      label: moduleTitle(value),
      value,
      disabled: false,
    });
  });

  return options;
}

function isSuperAdminUserType(userType) {
  const userTypeName = userType?.user_type_name ?? userType?.userTypeName ?? userType?.name;

  // Identify SUPER ADMIN by NAME only.
  //
  // This used to also treat `user_type_id === 1` as super admin. Role ids are
  // per-deployment: in the migrated US database role 1 is RECRUITER, so that
  // check silently hid all 135 recruiters from the users list AND from the
  // "User Type" picker when adding a user — they could be neither seen nor
  // assigned. A magic id cannot identify a role across databases; the name can.
  return normalizeRoleName(userTypeName) === 'SUPER ADMIN';
}

function getUserDisplayName(user) {
  const name = `${user?.first_name ?? user?.firstName ?? ''} ${user?.last_name ?? user?.lastName ?? ''}`.trim();
  return name || user?.username || user?.userName || user?.email || `User ${user?.user_id ?? user?.userId ?? ''}`;
}

function getPermissionRows(groups) {
  return groups.flatMap((menu) => {
    const menuId = menu.menu_id ?? menu.menuId ?? menu.MenuID;
    const menuName = menu.menu_name ?? menu.menuName ?? menu.MenuName ?? `Menu ${menuId ?? ''}`;
    const menuKey = menu.menu_key ?? menu.menuKey ?? menu.MenuKey ?? '';
    const permissions = menu.permissions ?? menu.Permissions ?? {};

    return Object.entries(permissions).map(([permissionMapKey, permission]) => {
      const permissionId = permission.permission_id ?? permission.permissionId ?? permission.PermissionID;
      const permissionKey = permission.permission_key
        ?? permission.permissionKey
        ?? permission.PermissionKey
        ?? permissionMapKey;
      const rawValue = permission.value ?? permission.Value ?? permission.access_value ?? permission.accessValue ?? permission;

      return {
        key: `${menuKey ?? menuName ?? menuId}:${permissionId ?? permissionKey}`,
        menuId,
        menuName,
        menuKey,
        permissionId,
        permissionKey,
        accessValue: Number(rawValue) === 1 ? 1 : 0,
      };
    });
  });
}

function permissionMenuIdentity(row) {
  return String(row.menuKey ?? row.menuName ?? row.menuId).trim().toLowerCase();
}

const REQUIRED_WORKFLOW_PERMISSION_ACTIONS = [
  // `sendApprove` is already persisted in role_menu_permissions for Onboarding
  // in older/current databases, but some deployments do not yet return it from
  // the menu-permission action master endpoint.  Keep the role editor capable
  // of rendering and saving that existing Mongo permission instead of silently
  // dropping it from the matrix.
  { permissionId: null, permissionKey: 'sendApprove', permissionName: 'Send Approve' },
];

function normalizePermissionActions(value) {
  const candidate = Array.isArray(value) ? value : value?.permissions ?? value?.actions ?? value?.data ?? value;
  const source = Array.isArray(candidate) ? candidate : Object.values(candidate ?? {});
  const normalized = source.map((action) => ({
    permissionId: action.permission_id ?? action.permissionId ?? action.legacyPermissionId ?? action.id ?? null,
    permissionKey: action.permission_key ?? action.permissionKey ?? action.key,
    permissionName: action.permission_name ?? action.permissionName ?? action.name ?? action.label,
  })).filter((action) => action.permissionKey);

  const existingKeys = new Set(normalized.map((action) => String(action.permissionKey).trim().toLowerCase()));
  REQUIRED_WORKFLOW_PERMISSION_ACTIONS.forEach((action) => {
    if (!existingKeys.has(action.permissionKey.toLowerCase())) normalized.push(action);
  });
  return normalized;
}

function permissionActionLabel(permission = {}) {
  const explicit = String(permission.permissionName ?? '').trim();
  if (explicit) return explicit;
  return String(permission.permissionKey ?? '')
    .replaceAll('_', ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function PermissionEditor({
  title,
  description,
  selectedName,
  loading,
  saving,
  rows,
  onChange,
  onMove,
  onSave,
}) {
  const menuRows = Object.values(rows.reduce((groups, row) => {
    const menuIdentity = permissionMenuIdentity(row);
    if (!groups[menuIdentity]) {
      groups[menuIdentity] = {
        key: menuIdentity,
        menuName: row.menuName,
        menuKey: row.menuKey,
        permissions: [],
      };
    }
    groups[menuIdentity].permissions.push(row);
    return groups;
  }, {}));

  const columns = [
    {
      title: 'Menu',
      dataIndex: 'menuName',
      width: 220,
      render: (value, row) => (
        <div className="admin-menu-order-cell">
          <div className="admin-menu-cell">
            <span>{value}</span>
            <Text type="secondary">{row.menuKey}</Text>
          </div>
          <Space size={2}>
            <Button size="small" type="text" icon={<ArrowUpOutlined />} onClick={() => onMove(row.key, -1)} aria-label={`Move ${value} up`} />
            <Button size="small" type="text" icon={<ArrowDownOutlined />} onClick={() => onMove(row.key, 1)} aria-label={`Move ${value} down`} />
          </Space>
        </div>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'permissions',
      render: (permissions) => (
        <div className="admin-permission-controls">
          {permissions
            .filter((permission) => isPermissionApplicable(
              permission.menuName ?? permission.menuKey,
              permission.permissionKey,
            ))
            .map((permission) => (
            <label className="admin-permission-control" key={permission.key}>
                <Checkbox
                  checked={permission.accessValue === 1}
                  onChange={(event) => onChange(permission.key, event.target.checked ? 1 : 0)}
                aria-label={`${permission.menuName} ${permission.permissionKey}`}
              >
                {permissionActionLabel(permission)}
              </Checkbox>
            </label>
          ))}
        </div>
      ),
    },
  ];

  return (
    <section className="admin-editor">
      <div className="admin-section-head">
        <div>
          <Title level={4}>{title}</Title>
          <Text type="secondary">{selectedName || description}</Text>
        </div>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={!selectedName || rows.length === 0}
          onClick={onSave}
        >
          Save
        </Button>
      </div>
      {loading ? (
        <div className="admin-loading">
          <Spin />
        </div>
      ) : menuRows.length ? (
        <Table
          size="small"
          rowKey="key"
          columns={columns}
          dataSource={menuRows}
          pagination={false}
          scroll={{ y: 420 }}
        />
      ) : (
        <Empty description="Select a record to customize permissions" />
      )}
    </section>
  );
}

function AdminDashboard({ onLogout }) {
  const [userForm] = Form.useForm();
  const [roleForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  // Server-side paging/search state for the users list. The users collection is
  // far too large to hold in the browser (6,134 rows here), and the previous
  // client-side approach could only ever see the first 500 — see listUsers().
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState(10);
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [userTypes, setUserTypes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [permissionActions, setPermissionActions] = useState([]);
  const [selectedUserTypeId, setSelectedUserTypeId] = useState(null);
  const [rolePermissionRows, setRolePermissionRows] = useState([]);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userSaving, setUserSaving] = useState(false);
  const [userDeletingId, setUserDeletingId] = useState(null);
  const [reportingUsers, setReportingUsers] = useState([]);
  const [reportingUsersLoading, setReportingUsersLoading] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleSaving, setRoleSaving] = useState(false);
  const [configTarget, setConfigTarget] = useState(null);
  const [configType, setConfigType] = useState('listView');
  const [seedOptions, setSeedOptions] = useState([]);
  const [seedModules, setSeedModules] = useState([]);
  const [seedLoading, setSeedLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const currentUser = getStoredUser();

  const availableSeedOptions = useMemo(
    () => seedOptions.filter((option) => !option.disabled),
    [seedOptions],
  );
  const allSeeded = !seedLoading && availableSeedOptions.length === 0;

  const loadSeedState = useCallback(async () => {
    setSeedLoading(true);
    try {
      const [status, collections] = await Promise.all([
        getFieldConfigSeedStatus(),
        getAvailableCollections(),
      ]);
      const options = buildSeedOptions(status.modules, collections);
      const allowed = new Set(options.filter((option) => !option.disabled).map((option) => option.value));
      setSeedOptions(options);
      setSeedModules((prev) => prev.filter((value) => allowed.has(value)));
    } catch {
      message.error('Failed to load seed status');
    } finally {
      setSeedLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSeedState();
  }, [loadSeedState]);

  async function handleSeedFieldConfig() {
    if (seedModules.length === 0) {
      message.warning(allSeeded ? 'All modules already seeded.' : 'Select at least one module to seed.');
      return;
    }

    setSeeding(true);
    try {
      await seedFieldConfig(seedModules);
      message.success('User type field config seeded');
      setSeedModules([]);
      await loadSeedState();
    } catch (error) {
      message.error(error?.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  }

  async function loadData() {
    setLoading(true);
    try {
      // Users are NOT loaded here — they have their own paged/searched effect
      // below, because they are the one list that cannot be held in memory.
      const [userTypesResult, menusResult, actionsResult] = await Promise.allSettled([
        listUserTypes(),
        listMenus(),
        listMenuPermissions(),
      ]);
      const userTypesData = userTypesResult.status === 'fulfilled' ? userTypesResult.value : [];

      if (userTypesResult.status === 'rejected') {
        message.error(userTypesResult.reason?.message || 'Unable to load user types');
      }

      setUserTypes(userTypesData ?? []);
      setMenus(menusResult.status === 'fulfilled' ? menusResult.value ?? [] : []);
      setPermissionActions(actionsResult.status === 'fulfilled' ? normalizePermissionActions(actionsResult.value) : []);
      setSelectedUserTypeId((userTypesData ?? [])[0]?.user_type_id ?? null);
    } catch (error) {
      message.error(error.message || 'Unable to load admin data');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    queueMicrotask(() => {
      loadData();
    });
  }, []);

  // Fetch ONE page of users, for the current search. Re-runs whenever the page,
  // page size or search term changes — the search is sent to the server, so it
  // matches across all 6,134 users rather than the handful already downloaded.
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const { users: rows, total } = await listUsers({
        limit: userPageSize,
        offset: (userPage - 1) * userPageSize,
        search: userSearch,
      });
      setUsers(rows ?? []);
      setUserTotal(total ?? 0);
    } catch (error) {
      message.error(error?.message || 'Unable to load users');
      setUsers([]);
      setUserTotal(0);
    } finally {
      setUsersLoading(false);
    }
  }, [userPage, userPageSize, userSearch]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  useEffect(() => {
    queueMicrotask(() => {
      if (!selectedUserTypeId) {
        setRolePermissionRows([]);
        return;
      }
      setPermissionLoading(true);
      console.log('DEBUG requesting role permissions for selectedUserTypeId=', selectedUserTypeId, 'localStorage scope=', {
        tenantId: localStorage.getItem('tenantId'),
        businessId: localStorage.getItem('businessId'),
        businessUnitId: localStorage.getItem('businessUnitId'),
      });
      getRoleMenuPermissions(selectedUserTypeId)
        .then((groups) => {
          console.log('DEBUG raw groups from /get-role-details', groups);
          const savedRows = getPermissionRows(groups ?? []);
          console.log('DEBUG savedRows (jobs only)', savedRows.filter((r) => permissionMenuIdentity(r) === 'jobs' || permissionMenuIdentity(r) === 'job'));
          const savedAccess = new Map(savedRows.map((row) => [
            `${permissionMenuIdentity(row)}:${String(row.permissionKey).toLowerCase()}`,
            row.accessValue,
          ]));
          console.log('DEBUG savedAccess keys', [...savedAccess.keys()]);
          console.log('DEBUG menus (jobs only)', menus.filter((m) => String(m.menu_key ?? m.menu_name ?? m.menu_id).trim().toLowerCase() === 'jobs'));
          const rows = menus.flatMap((menu) => permissionActions.map((action) => {
            const menuIdentity = String(menu.menu_key ?? menu.menu_name ?? menu.menu_id).trim().toLowerCase();
            const permissionKey = String(action.permissionKey).toLowerCase();
            return {
              key: `${menu.menu_key ?? menu.menu_name ?? menu.menu_id}:${action.permissionId ?? action.permissionKey}`,
              menuId: menu.menu_id,
              menuName: menu.menu_name,
              menuKey: menu.menu_key,
              permissionId: action.permissionId,
              permissionKey: action.permissionKey,
              accessValue: savedAccess.get(`${menuIdentity}:${permissionKey}`) ?? 0,
            };
          }));
          console.log('DEBUG final rows (jobs only)', rows.filter((r) => permissionMenuIdentity(r) === 'jobs' || permissionMenuIdentity(r) === 'job'));
          setRolePermissionRows(rows);
        })
        .catch((error) => message.error(error.message || 'Unable to load role permissions'))
        .finally(() => setPermissionLoading(false));
    });
  }, [selectedUserTypeId, menus, permissionActions]);

  const selectedUserType = useMemo(
    () => userTypes.find((userType) => userType.user_type_id === selectedUserTypeId),
    [selectedUserTypeId, userTypes],
  );

  const userTypeNameById = useMemo(() => (
    userTypes.reduce((lookup, userType) => ({
      ...lookup,
      [userType.user_type_id]: userType.user_type_name,
    }), {})
  ), [userTypes]);

  // Declared here, beside the other id -> name lookup, because
  // reportingManagerOptions below reads it. A `const` is in the temporal dead
  // zone until its own line runs, and a useMemo's dependency array is evaluated
  // AT the useMemo call — so declaring this after that consumer threw
  // "Cannot access 'userNameById' before initialization" and took the whole
  // Admin route down with it.
  const userNameById = useMemo(
    () => users.reduce((lookup, user) => ({
      ...lookup,
      [user.user_id]: getUserDisplayName(user),
    }), {}),
    [users],
  );

  const manageableUserTypes = useMemo(
    () => userTypes.filter((userType) => !isSuperAdminUserType(userType)),
    [userTypes],
  );

  const manageableUsers = useMemo(
    () => users.filter((user) => !isSuperAdminUserType({
      user_type_id: user.user_type_id,
      user_type_name: userTypeNameById[user.user_type_id],
    })),
    [userTypeNameById, users],
  );

  useEffect(() => {
    if (!userModalOpen) {
      setReportingUsers([]);
      return;
    }

    const roleId = Number(userForm.getFieldValue('user_type_id') ?? 0);
    if (!roleId) {
      setReportingUsers([]);
      return;
    }

    let cancelled = false;
    setReportingUsersLoading(true);
    getUsersFromRole(roleId)
      .then((usersForRole) => {
        if (cancelled) return;
        setReportingUsers(
          usersForRole.filter((user) => !editingUser || user.user_id !== editingUser.user_id),
        );
      })
      .catch((error) => {
        if (!cancelled) {
          message.error(error.message || 'Unable to load reporting users');
          setReportingUsers([]);
        }
      })
      .finally(() => {
        if (!cancelled) setReportingUsersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editingUser, userForm, userModalOpen]);

  // Options a user may report to, per the role chain (/get-users returns the
  // roles ABOVE this one, resolved through roles.reportingRoleId).
  //
  // The user's CURRENT manager is always included, even when the chain does not
  // offer them. A Select renders the RAW VALUE when no option matches, so
  // without this the form showed "166" where the list correctly showed
  // "PourushRealtek" — the id was never meant to be user-visible. It happens
  // whenever the stored manager sits outside the chain: employer logins (whose
  // roleId is 0, so the chain is empty and the fetch is skipped entirely), and
  // any user whose role or manager changed after the link was made.
  //
  // Showing the stored manager is also the honest thing to do: it is what will
  // be saved if the field is left alone, so hiding it would misrepresent the
  // record.
  const reportingManagerOptions = useMemo(() => {
    const options = reportingUsers.map((user) => ({
      label: getUserDisplayName(user),
      value: user.user_id,
    }));
    const currentId = Number(editingUser?.reporting_id ?? editingUser?.reportingId ?? 0);
    if (!currentId) return options;
    if (options.some((option) => Number(option.value) === currentId)) return options;
    // reportingName is resolved server-side and is what the LIST already shows.
    const label = editingUser?.reportingName
      ?? editingUser?.reporting_name
      ?? userNameById[currentId]
      ?? `User ${currentId}`;
    return [{ label, value: currentId }, ...options];
  }, [editingUser, reportingUsers, userNameById]);

  // The same rule for User Type. Employer logins carry roleId 0, which is in no
  // role list, so the control showed a bare "0". Their own userType ("Employer")
  // is the meaningful label until the role itself exists in the data.
  const userTypeOptions = useMemo(() => {
    const options = manageableUserTypes.map((userType) => ({
      label: userType.user_type_name,
      value: userType.user_type_id,
    }));
    if (!editingUser) return options;
    const currentId = Number(editingUser.user_type_id ?? 0);
    if (options.some((option) => Number(option.value) === currentId)) return options;
    const label = userTypeNameById[currentId]
      ?? editingUser.userType
      ?? editingUser.roleName
      ?? (currentId > 0 ? `Type ${currentId}` : 'Unassigned');
    return [{ label, value: currentId }, ...options];
  }, [editingUser, manageableUserTypes, userTypeNameById]);

  const metrics = [
    // The SERVER's count for the whole collection — manageableUsers is now just
    // the current page, so its length would read as "10 users".
    { label: 'Users', value: userTotal },
    { label: 'User Types', value: userTypes.length },
    { label: 'Menus', value: menus.length },
  ];

  const configurationItems = [
    {
      key: 'form-groups',
      label: <span><AppstoreAddOutlined /> Form Groups</span>,
      title: 'Form Groups',
      description: 'Build module add and edit form groups with validations, dropdowns, and custom forms.',
      component: <FormGroupsSection />,
    },
    {
      key: 'detail-groups',
      label: <span><BgColorsOutlined /> Detail Groups</span>,
      title: 'Detail Groups',
      description: 'Control detail-view labels, rendering, lookups, merged fields, and display defaults.',
      component: <DetailGroupsSection />,
    },
    {
      key: 'actions',
      label: <span><ThunderboltOutlined /> Actions</span>,
      title: 'Actions',
      description: 'Manage row action buttons and their visibility rules from one workspace.',
      component: <ActionsSection />,
    },
    {
      key: 'templates',
      label: <span><MailOutlined /> Templates</span>,
      title: 'Templates',
      description: 'Manage reusable email templates and signatures.',
      component: <TemplatesSection />,
    },
    {
      key: 'ai-services',
      label: <span><ThunderboltOutlined /> AI Services</span>,
      title: 'AI Services',
      description: 'Register the AI and automation services used by admin-configured AI actions.',
      component: <AiServiceSettingsSection />,
    },
    {
      key: 'bgc-integration',
      label: <span><SafetyCertificateOutlined /> Integrations</span>,
      title: 'Integrations',
      description: 'Manage manual and API integrations used across application workflows.',
      component: <IntegrationSettingsSection />,
    },
    {
      key: 'automations',
      label: <span><ApartmentOutlined /> Automations</span>,
      title: 'Automations',
      description: 'Reference ids, duplicate checks, cross-module snapshots, reporting-chain hierarchy expansion, board updates, notifications, and cache invalidation — per module, config-driven.',
      component: <AutomationsSection />,
    },
    {
      key: 'workflows',
      label: <span><ApartmentOutlined /> Workflows</span>,
      title: 'Workflows',
      description: 'Event-driven behavior per module (beforeCreate/afterCreate/…): the metadata the platform Workflow Engine executes — where the jobs, candidates, and submissions create flows live.',
      component: <WorkflowsSection />,
    },
    {
      key: 'masters',
      label: <span><AppstoreAddOutlined /> Masters</span>,
      title: 'Masters',
      description: 'Shared Location and Tax lists used across modules (e.g. the submission form’s W2 employer section).',
      component: <MastersSection />,
    },
  ];

  const adminSidebarItems = [
    {
      key: 'users',
      icon: <UserOutlined />,
      label: 'Users',
    },
    {
      key: 'roles',
      icon: <TeamOutlined />,
      label: 'User Types',
    },
    {
      key: 'menus',
      icon: <MenuOutlined />,
      label: 'Menus',
    },
    {
      key: 'configurations',
      icon: <AppstoreAddOutlined />,
      label: 'Configurations',
    },
    {
      key: 'integrations',
      icon: <SafetyCertificateOutlined />,
      label: 'Integrations',
    },
  ];

  function updateRolePermission(key, value) {
    setRolePermissionRows((rows) => {
      const matchCount = rows.filter((row) => row.key === key).length;
      console.log('DEBUG updateRolePermission', { key, value, matchCount });
      return rows.map((row) => (
        row.key === key ? { ...row, accessValue: value } : row
      ));
    });
  }

  async function saveRolePermissions() {
    setSaving(true);
    try {
      console.log('DEBUG saveRolePermissions jobs rows', rolePermissionRows.filter((row) => permissionMenuIdentity(row) === 'jobs' || permissionMenuIdentity(row) === 'job'));
      await saveRoleMenuPermissions(selectedUserTypeId, rolePermissionRows.map((row) => ({
        menu_id: row.menuId,
        menu_name: row.menuName,
        menu_key: row.menuKey,
        permission_id: row.permissionId,
        permission_key: row.permissionKey,
        access_value: row.accessValue,
        updated_by: currentUser?.userId ?? null,
      })));
      message.success('Role permissions updated');
    } catch (error) {
      message.error(error.message || 'Unable to save role permissions');
    } finally {
      setSaving(false);
    }
  }

  function handleLogout() {
    logout();
    onLogout?.();
  }

  function openCreateUser() {
    setEditingUser(null);
    userForm.resetFields();
    userForm.setFieldsValue({
      status: 'ACTIVE',
      user_type_id: manageableUserTypes[0]?.user_type_id,
      reporting_id: 0,
    });
    setUserModalOpen(true);
  }

  function openEditUser(user) {
    setEditingUser(user);
    userForm.setFieldsValue({
      first_name: user.first_name,
      last_name: user.last_name,
      username: user.username,
      email: user.email,
      mobile: user.mobile,
      user_type_id: user.user_type_id,
      reporting_id: user.reporting_id ?? user.reportingId ?? 0,
      profile_image: user.profile_image,
      status: user.status,
    });
    setUserModalOpen(true);
  }

  async function submitUser(values) {
    setUserSaving(true);
    try {
      const payload = {
        ...values,
        // The auth API's UserMaster.status field is a Go string. Ant Select
        // values or older saved form state may still provide 1/0.
        status: values.status === 1 || values.status === '1' || values.status === true
          ? 'ACTIVE'
          : values.status === 0 || values.status === '0' || values.status === false
            ? 'INACTIVE'
            : String(values.status ?? 'ACTIVE').toUpperCase(),
        updated_by: currentUser?.userId ?? null,
        created_by: currentUser?.userId ?? null,
      };
      if (!payload.password) {
        delete payload.password;
      }
      if (editingUser) {
        await updateUser(editingUser.user_id, payload);
        message.success('User updated');
      } else {
        await createUser(payload);
        message.success('User created');
      }
      setUserModalOpen(false);
      setEditingUser(null);
      await loadUsers();
    } catch (error) {
      message.error(error.message || 'Unable to save user');
    } finally {
      setUserSaving(false);
    }
  }

  async function handleDeleteUser(userId) {
    setUserDeletingId(userId);
    try {
      await deleteUser(userId);
      message.success('User deleted');
      await loadUsers();
    } catch (error) {
      message.error(error.message || 'Unable to delete user');
    } finally {
      setUserDeletingId(null);
    }
  }

  function openFieldConfig(kind, record) {
    setConfigType('listView');
    setConfigTarget({ kind, record });
    if (kind === 'role') setSelectedUserTypeId(record.user_type_id);
  }

  function moveRoleMenu(menuIdentity, direction) {
    setRolePermissionRows((rows) => {
      const identities = [...new Set(rows.map(permissionMenuIdentity))];
      const from = identities.indexOf(menuIdentity);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= identities.length) return rows;
      [identities[from], identities[to]] = [identities[to], identities[from]];
      const rank = new Map(identities.map((identity, index) => [identity, index]));
      return [...rows].sort((a, b) => rank.get(permissionMenuIdentity(a)) - rank.get(permissionMenuIdentity(b)));
    });
  }

  function openCreateRole() {
    setEditingRole(null);
    roleForm.resetFields();
    roleForm.setFieldsValue({ status: 'ACTIVE' });
    setRoleModalOpen(true);
  }

  function openEditRole(role) {
    setEditingRole(role);
    setRoleModalOpen(true);
    roleForm.setFieldsValue(role);
  }

  async function submitRole(values) {
    setRoleSaving(true);
    try {
      const audit = currentUser?.userId ?? null;
      const isEditing = Boolean(editingRole);
      const role = isEditing
        ? await updateRole(editingRole.user_type_id, { ...values, updated_by: audit })
        : await createRole({ ...values, created_by: audit, visible_menu_ids: [] });
      const roleId = role.user_type_id;
      message.success(isEditing ? 'User type updated' : 'User type created. Customize its menus and permissions.');
      setRoleModalOpen(false);
      await loadData();
      setSelectedUserTypeId(roleId);
      if (!isEditing) {
        setConfigType('permissions');
        setConfigTarget({
          kind: 'role',
          record: {
            ...values,
            ...role,
            user_type_id: roleId,
            user_type_name: role.user_type_name ?? values.user_type_name,
          },
        });
      }
    } catch (error) {
      message.error(error.message || 'Unable to save role');
    } finally {
      setRoleSaving(false);
    }
  }

  async function handleDeleteRole(roleId) {
    try {
      await deleteRole(roleId);
      message.success('Role deleted');
      await loadData();
    } catch (error) {
      message.error(error.message || 'Unable to delete role');
    }
  }

  const userFields = [
    { label: 'Name', value: 'first_name', render: (value, row) => `${value ?? ''} ${row.last_name ?? ''}`.trim() || row.username },
    { label: 'Email', value: 'email' },
    {
      label: 'User Type',
      value: 'user_type_id',
      width: 160,
      // A raw id is never a useful thing to show a human. Provisioned employer
      // logins carry roleId 0, which matches no role, so this column rendered
      // literally "Type 0" for the 6,000 of them. Fall back to the user's own
      // userType ("Employer") and only then to a dash — the id is meaningless
      // to the person reading the screen either way.
      render: (value, row) => userTypeNameById[value]
        || row?.userType
        || row?.user_type
        || (Number(value) > 0 ? `Type ${value}` : '—'),
    },
    {
      label: 'Reporting Manager',
      value: 'reporting_id',
      width: 180,
      // userNameById can only resolve people on the CURRENT page now that users
      // are server-paged, so an unresolved id shows the id rather than lying
      // with "None" — which would read as "no manager" when there is one.
      render: (value, row) => userNameById[value]
        || row?.reportingName
        || row?.reporting_name
        || (Number(value) > 0 ? `User ${value}` : 'None'),
    },
    { label: 'Status', value: 'status', width: 110, sorter: false, render: (value) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag> },
    {
      label: 'Actions',
      value: '_actions',
      width: 150,
      sorter: false,
      render: (_, row) => (
        <Space size={4}>
          <Button
            aria-label="Edit user"
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEditUser(row)}
          />
          <Popconfirm
            title="Delete user?"
            description="This removes the user from the auth database."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteUser(row.user_id)}
          >
            <Button
              danger
              aria-label="Delete user"
              icon={<DeleteOutlined />}
              loading={userDeletingId === row.user_id}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const userTypeFields = [
    { label: 'User Type', value: 'user_type_name' },
    { label: 'Description', value: 'description' },
    { label: 'Status', value: 'status', width: 110, sorter: false, render: (value) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag> },
    {
      label: 'Actions',
      value: '_actions',
      width: 260,
      sorter: false,
      render: (_, row) => {
        const isCustomRole = Number(row.role_type) === 1;
        return (
          <Space size={4}>
          <Button aria-label="Configure role" size="small" onClick={() => openFieldConfig('role', row)}>Config</Button>
          <Button aria-label="Edit role" disabled={!isCustomRole} icon={<EditOutlined />} size="small" onClick={() => openEditRole(row)} />
          {isCustomRole && !isSuperAdminUserType(row) && (
            <Popconfirm title="Delete role?" onConfirm={() => handleDeleteRole(row.user_type_id)}>
              <Button aria-label="Delete role" danger icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
          )}
        </Space>
        );
      },
    },
  ];

  return (
    <Layout className="admin-shell">
      <Sider width={248} className="admin-sider">
        {/* <div className="admin-brand">
          <img src="/favicon.svg" alt="" />
          <div>
            <strong>Zinnext</strong>
            <span>Admin Core</span>
          </div>
        </div>
        <div className="admin-profile">
          <Text>{currentUser?.username || 'Admin'}</Text>
          <span>{currentUser?.email}</span>
        </div> */}
        <div className="admin-sidebar-nav">
          <Text className="admin-sidebar-title">Admin Menu</Text>
          <AntMenu
            theme="dark"
            mode="inline"
            selectedKeys={[activeTab]}
            onClick={({ key }) => setActiveTab(key)}
            items={adminSidebarItems}
          />
        </div>
      </Sider>
      <Layout>
        {/* <Header className="admin-header">
          <div>
            <Title level={3}>Admin</Title>
            <Text type="secondary">Manage access and CORE-THEME configuration.</Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadData}>Refresh</Button>
            <Button icon={<LogoutOutlined />} onClick={handleLogout}>Logout</Button>
          </Space>
        </Header> */}
        <Content className="admin-content">
          <>
            {/* <div className="admin-metrics">
              {metrics.map((item) => (
                <div className="admin-metric" key={item.label}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div> */}
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              tabBarStyle={{ display: 'none' }}
              items={[
                  {
                    key: 'users',
                    label: <span><UserOutlined /> Users</span>,
                    children: (
                      <div className="admin-stack">
                        <section className="admin-section">
                          <div className="admin-section-head">
                            <div>
                              <Title level={4}>Users</Title>
                              <Text type="secondary">Create and manage users with non-super-admin user types.</Text>
                            </div>
                            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateUser}>
                              Add
                            </Button>
                          </div>
                          <ParamListView
                            listName="Users"
                            loading={usersLoading}
                            fields={userFields}
                            dataSource={manageableUsers.map((user) => ({ ...user, key: user.user_id }))}
                            // Server-driven: `total` is the count for the whole
                            // query, not the rows in hand, and onSearch hands the
                            // term to the API instead of filtering locally.
                            total={userTotal}
                            current={userPage}
                            pageSize={userPageSize}
                            onPageChange={setUserPage}
                            onPageSizeChange={(size) => { setUserPageSize(size); setUserPage(1); }}
                            onSearch={(term) => { setUserSearch(term ?? ''); setUserPage(1); }}
                            rowSelection={false}
                          />
                        </section>
                      </div>
                    ),
                  },
                  {
                    key: 'roles',
                    label: <span><TeamOutlined /> User Types</span>,
                    children: (
                      <div className="admin-stack">
                        <AppCard
                          icon={<AppstoreOutlined />}
                          title="Field Config Seed"
                          style={{ marginBottom: 12 }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ minWidth: 320, flex: 1 }}>
                              <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                value={seedModules}
                                options={seedOptions}
                                placeholder={allSeeded ? 'All modules already seeded' : 'Select modules or collections to seed'}
                                disabled={seedLoading || seeding || allSeeded}
                                loading={seedLoading}
                                onChange={setSeedModules}
                                style={{ width: '100%' }}
                              />
                            </div>
                            <Button
                              type="primary"
                              loading={seeding}
                              disabled={seedLoading || seedModules.length === 0}
                              onClick={handleSeedFieldConfig}
                            >
                              Seed Field Config
                            </Button>
                          </div>
                          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                            {allSeeded
                              ? 'Seed is disabled because all configured modules already have user type field config data.'
                              : 'Already seeded modules are disabled. New modules or collections can still be seeded.'}
                          </Text>
                        </AppCard>
                        <section className="admin-section">
                          <div className="admin-section-head">
                            <div>
                              <Title level={4}>User Types</Title>
                              <Text type="secondary">Role level permissions are inherited by users.</Text>
                            </div>
                            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateRole}>Add Role</Button>
                          </div>
                          <ParamListView
                            listName="User Types"
                            loading={loading}
                            fields={userTypeFields}
                            dataSource={userTypes.map((role) => ({
                              ...role,
                              key: role.user_type_id,
                              roleId: role.user_type_id,
                              roleName: role.user_type_name,
                            }))}
                            total={userTypes.length}
                            rowSelection={false}
                          />
                        </section>
                      </div>
                    ),
                  },
                  {
                    key: 'menus',
                    label: <span><MenuOutlined /> Menus</span>,
                    children: <MenusSection />,
                  },
                  {
                    key: 'configurations',
                    label: <span><AppstoreAddOutlined /> Configurations</span>,
                    children: (
                      <section className="admin-section admin-config-workspace">
                        <div className="admin-section-head">
                          <div>
                            <Title level={4}>Configuration Center</Title>
                            <Text type="secondary">CORE-THEME admin modules embedded into the admin dashboard.</Text>
                          </div>
                          <Tag color="blue">{configurationItems.length} configurations</Tag>
                        </div>
                        <Tabs
                          className="admin-config-tabs"
                          defaultActiveKey="form-groups"
                          tabPosition="left"
                          items={configurationItems.map((item) => ({
                            key: item.key,
                            label: item.label,
                            children: (
                              <div className="admin-config-panel">
                                <div className="admin-config-panel-head">
                                  <div>
                                    <Title level={4}>{item.title}</Title>
                                    <Text type="secondary">{item.description}</Text>
                                  </div>
                                </div>
                                <div className="admin-config-panel-body">
                                  {item.component}
                                </div>
                              </div>
                            ),
                          }))}
                        />
                      </section>
                    ),
                  },
                  {
                    key: 'integrations',
                    label: <span><SafetyCertificateOutlined /> Integrations</span>,
                    children: (
                      <section className="admin-section admin-config-workspace">
                        <div className="admin-section-head">
                          <div>
                            <Title level={4}>Integrations</Title>
                            <Text type="secondary">Configure external services used by onboarding and other workflows.</Text>
                          </div>
                        </div>
                        <div className="admin-config-panel">
                          <div className="admin-config-panel-body">
                            <IntegrationSettingsSection />
                          </div>
                        </div>
                      </section>
                    ),
                  },
              ]}
            />
          </>
          <Modal
            className="uss-modal"
            title={`Configure User Type: ${configTarget?.record?.user_type_name ?? ''}`}
            open={Boolean(configTarget)}
            onCancel={() => setConfigTarget(null)}
            footer={null}
            destroyOnHidden
            width={900}
          >
            <div className="uss-config-toggle" style={{ marginBottom: 12 }}>
              {[
                { key: 'listView', label: 'List View' },
                { key: 'filter', label: 'Filter' },
                { key: 'form', label: 'Form' },
                { key: 'permissions', label: 'Permissions' },
                { key: 'actions', label: 'Actions' },
              ].map((item) => (
                <button
                  type="button"
                  key={item.key}
                  className={`uss-config-btn${configType === item.key ? ' active' : ''}`}
                  onClick={() => setConfigType(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            {configTarget && configType === 'permissions' ? (
              <PermissionEditor
                title="Role Permissions"
                description="Configure menu permissions for this user type"
                selectedName={selectedUserType?.user_type_name}
                loading={permissionLoading}
                saving={saving}
                rows={rolePermissionRows}
                onChange={updateRolePermission}
                onMove={moveRoleMenu}
                onSave={saveRolePermissions}
              />
            ) : configTarget && configType === 'actions' ? (
              <RoleActionsEditor
                key={`${configTarget.record?.user_type_id}-actions`}
                role={{
                  ...configTarget.record,
                  roleId: configTarget.record.user_type_id,
                  roleName: configTarget.record.user_type_name,
                }}
              />
            ) : configTarget && configType === 'form' ? (
              <RoleFormPermissionsEditor
                key={`${configTarget.record?.user_type_id}-form`}
                role={{
                  ...configTarget.record,
                  roleId: configTarget.record.user_type_id,
                  roleName: configTarget.record.user_type_name,
                }}
              />
            ) : configTarget ? (
              <ModuleFieldConfigEditor
                key={`${configTarget.record?.user_type_id}-${configType}`}
                role={{
                  ...configTarget.record,
                  roleId: configTarget.record.user_type_id,
                  roleName: configTarget.record.user_type_name,
                }}
                configType={configType}
              />
            ) : null}
          </Modal>
          <Modal
            className="admin-role-modal"
            title={editingRole ? 'Edit User Type' : 'Create User Type'}
            open={roleModalOpen}
            onCancel={() => setRoleModalOpen(false)}
            onOk={() => roleForm.submit()}
            okText={editingRole ? 'Save' : 'Next'}
            confirmLoading={roleSaving}
            destroyOnHidden
            width={900}
          >
            <Form form={roleForm} layout="vertical" onFinish={submitRole}>
              <Form.Item name="user_type_name" label="Role name" rules={[{ required: true, message: 'Role name is required' }]}>
                <Input placeholder="Recruiter" />
              </Form.Item>
              <Form.Item name="description" label="Description"><Input.TextArea rows={3} /></Form.Item>
              <Form.Item name="status" label="Status">
                <Select options={[{ value: 'ACTIVE' }, { value: 'INACTIVE' }]} />
              </Form.Item>
            </Form>
          </Modal>
          <Modal
            // Presentation hook only — a Modal renders in a PORTAL, outside
            // .admin-shell, so admin styles cannot reach it without a class.
            className="admin-user-modal"
            title={editingUser ? 'Edit User' : 'Add User'}
            open={userModalOpen}
            onCancel={() => setUserModalOpen(false)}
            onOk={() => userForm.submit()}
            confirmLoading={userSaving}
            destroyOnHidden
          >
            <Form
              form={userForm}
              layout="vertical"
              requiredMark={false}
              onFinish={submitUser}
            >
              <div className="admin-form-row">
                <Form.Item
                  label="First Name"
                  name="first_name"
                  rules={[{ required: true, message: 'First name is required' }]}
                >
                  <Input placeholder="John" />
                </Form.Item>
                <Form.Item label="Last Name" name="last_name">
                  <Input placeholder="Doe" />
                </Form.Item>
              </div>
              <Form.Item
                label="Username"
                name="username"
                rules={[{ required: true, message: 'Username is required' }]}
              >
                <Input placeholder="john.doe" />
              </Form.Item>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Enter a valid email' },
                ]}
              >
                <Input placeholder="john@example.com" />
              </Form.Item>
              <Form.Item
                label={editingUser ? 'Password' : 'Password'}
                name="password"
                rules={editingUser ? [] : [{ required: true, message: 'Password is required' }]}
              >
                <Input.Password placeholder={editingUser ? 'Leave empty to keep current password' : 'Password'} />
              </Form.Item>
              <div className="admin-form-row">
                <Form.Item label="Mobile" name="mobile">
                  <Input placeholder="9999999999" />
                </Form.Item>
                <Form.Item
                  label="User Type"
                  name="user_type_id"
                  rules={[{ required: true, message: 'User type is required' }]}
                >
                  <Select
                    placeholder="Select user type"
                    onChange={() => {
                      setReportingUsers([]);
                      queueMicrotask(() => {
                        const roleId = Number(userForm.getFieldValue('user_type_id') ?? 0);
                        if (roleId) {
                          setReportingUsersLoading(true);
                          getUsersFromRole(roleId)
                            .then((usersForRole) => {
                              setReportingUsers(
                                usersForRole.filter((user) => !editingUser || user.user_id !== editingUser.user_id),
                              );
                            })
                            .catch((error) => {
                              message.error(error.message || 'Unable to load reporting users');
                              setReportingUsers([]);
                            })
                            .finally(() => {
                              setReportingUsersLoading(false);
                            });
                        }
                      });
                    }}
                    options={userTypeOptions}
                  />
                </Form.Item>
              </div>
              <Form.Item label="Reporting Manager" name="reporting_id">
                <Select
                  allowClear
                  showSearch
                  loading={reportingUsersLoading}
                  placeholder="Select reporting manager"
                  optionFilterProp="label"
                  options={[
                    { label: 'None', value: 0 },
                    ...reportingManagerOptions,
                  ]}
                />
              </Form.Item>
              <Form.Item label="Profile Image" name="profile_image">
                <Input placeholder="https://..." />
              </Form.Item>
              <Form.Item label="Status" name="status">
                <Select
                  options={[
                    { label: 'ACTIVE', value: 'ACTIVE' },
                    { label: 'INACTIVE', value: 'INACTIVE' },
                  ]}
                />
              </Form.Item>
            </Form>
          </Modal>
        </Content>
      </Layout>
    </Layout>
  );
}

export default AdminDashboard;
